import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { ApiContext } from '../../contexts/ApiContext';
import { useNotification } from '../../contexts/NotificationContext';
import SuperAdminService from '../../service/super-admin.service';
import { accountPath } from '../../utils/accountPath';
import { formatPhone, maskPhoneInput, normalizePhone } from '../../utils/phone';

const ACCOUNT_TYPES = [
  { value: 'RESTAURANTE', label: 'Restaurante' },
  { value: 'PIZZARIA', label: 'Pizzaria' },
  { value: 'HAMBURGUERIA', label: 'Hamburgueria' },
  { value: 'ACAI', label: 'Açaí' },
  { value: 'CONFEITARIA', label: 'Confeitaria' },
  { value: 'DISTRIBUIDORA', label: 'Distribuidora' },
  { value: 'OUTRO', label: 'Outro' },
];

const WHATSAPP_PROVIDERS = [
  { value: 'baileys', label: 'Baileys — QR Code' },
  { value: 'waba', label: 'WhatsApp Cloud API oficial — WABA' },
];

const ACCOUNT_TYPE_LABEL = {
  ...Object.fromEntries(ACCOUNT_TYPES.map(({ value, label }) => [value, label])),
  SORVETERIA: 'Açaí',
};

function formatMoney(cents) {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('pt-BR');
}

const emptyCreateForm = {
  name: '',
  cnpj: '',
  phone: '',
  type: 'RESTAURANTE',
  planId: '',
  whatsappProvider: 'baileys',
  adminName: '',
  adminEmail: '',
  adminPassword: '',
};

function SuperAdminPage() {
  const navigate = useNavigate();
  const { user } = useContext(ApiContext);
  const { notify } = useNotification();

  const [stats, setStats] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [creating, setCreating] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editAccount, setEditAccount] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const [ownPasswordOpen, setOwnPasswordOpen] = useState(false);
  const [ownPassword, setOwnPassword] = useState('');
  const [savingOwnPassword, setSavingOwnPassword] = useState(false);
  const [plans, setPlans] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statsData, accountsData] = await Promise.all([
        SuperAdminService.getStats(),
        SuperAdminService.listAccounts(search.trim() || undefined),
      ]);
      setStats(statsData);
      setAccounts(Array.isArray(accountsData) ? accountsData : []);
    } catch (err) {
      notify('tr', 'danger', 'Erro ao carregar dados do painel', 'icon-alert-circle-exc');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, notify]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    SuperAdminService.listPlans(true)
      .then((data) => setPlans(Array.isArray(data) ? data : []))
      .catch(() => setPlans([]));
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { planId, ...rest } = createForm;
      await SuperAdminService.createAccount({
        ...rest,
        phone: normalizePhone(createForm.phone),
        ...(planId !== '' && planId != null ? { planId: Number(planId) } : {}),
      });
      notify('tr', 'success', 'Conta criada com sucesso', 'icon-check-2');
      setCreateOpen(false);
      setCreateForm(emptyCreateForm);
      load();
    } catch (err) {
      const msg = err?.response?.data?.message;
      notify('tr', 'danger', Array.isArray(msg) ? msg.join(', ') : msg || 'Erro ao criar conta', 'icon-alert-circle-exc');
    } finally {
      setCreating(false);
    }
  };

  const selectedCreatePlan = plans.find((p) => p.id === Number(createForm.planId));
  const selectedCreatePlanFeatures = (selectedCreatePlan?.features || []).filter((f) => f.enabled);

  const openEdit = (acc) => {
    setEditAccount(acc);
    setEditForm({
      name: acc.name,
      cnpj: acc.cnpj,
      type: acc.type,
      phone: acc.phone ? formatPhone(acc.phone) : '',
      planId: acc.planId ?? '',
      aiChatMonthlyLimit: acc.aiChatMonthlyLimit ?? 100,
      botEnabled: !!acc.botEnabled,
      whatsappProvider: acc.whatsappProvider || 'baileys',
    });
    setEditOpen(true);
  };

  const selectedEditPlan = plans.find((p) => p.id === Number(editForm.planId));
  const selectedPlanHasAiChat = !!(selectedEditPlan?.features || []).find(
    (f) => f.featureKey === 'ai_advanced_chat' && f.enabled,
  );
  const selectedPlanFeatures = (selectedEditPlan?.features || []).filter((f) => f.enabled);

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editAccount) return;
    const providerChanged = editForm.whatsappProvider !== (editAccount.whatsappProvider || 'baileys');
    if (
      providerChanged &&
      !window.confirm(
        'Trocar o provedor desconecta o WhatsApp atual desta conta. O estabelecimento vai precisar conectar de novo. Continuar?',
      )
    ) {
      return;
    }
    setSavingEdit(true);
    try {
      const planId = editForm.planId === '' || editForm.planId == null
        ? null
        : Number(editForm.planId);
      await SuperAdminService.updateAccount(editAccount.id, {
        name: editForm.name,
        cnpj: editForm.cnpj,
        type: editForm.type,
        phone: normalizePhone(editForm.phone) || null,
        planId,
        botEnabled: !!editForm.botEnabled,
        whatsappProvider: editForm.whatsappProvider,
        aiChatMonthlyLimit: selectedPlanHasAiChat
          ? Number(editForm.aiChatMonthlyLimit) || 100
          : null,
      });

      notify('tr', 'success', 'Conta atualizada', 'icon-check-2');
      setEditOpen(false);
      load();
    } catch (err) {
      const msg = err?.response?.data?.message;
      notify(
        'tr',
        'danger',
        Array.isArray(msg) ? msg.join(', ') : msg || 'Erro ao atualizar conta',
        'icon-alert-circle-exc',
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const toggleBlock = async (acc) => {
    const blocked = !acc.adminBlocked;
    const label = blocked ? 'bloquear' : 'desbloquear';
    if (!window.confirm(`Deseja ${label} a conta "${acc.name}"?`)) return;
    try {
      await SuperAdminService.updateAccount(acc.id, { adminBlocked: blocked });
      notify('tr', 'success', blocked ? 'Conta bloqueada' : 'Conta desbloqueada', 'icon-check-2');
      load();
    } catch {
      notify('tr', 'danger', 'Erro ao alterar bloqueio', 'icon-alert-circle-exc');
    }
  };

  const openResetPassword = (acc) => {
    setPasswordTarget(acc);
    setNewPassword('');
    setPasswordOpen(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!passwordTarget || newPassword.length < 6) return;
    setSavingPassword(true);
    try {
      await SuperAdminService.resetAdminPassword(passwordTarget.id, newPassword);
      notify('tr', 'success', 'Senha do admin redefinida', 'icon-check-2');
      setPasswordOpen(false);
    } catch {
      notify('tr', 'danger', 'Erro ao redefinir senha', 'icon-alert-circle-exc');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleOwnPassword = async (e) => {
    e.preventDefault();
    if (ownPassword.length < 6) return;
    setSavingOwnPassword(true);
    try {
      await SuperAdminService.changeOwnPassword(ownPassword);
      notify('tr', 'success', 'Sua senha foi alterada', 'icon-check-2');
      setOwnPasswordOpen(false);
      setOwnPassword('');
    } catch {
      notify('tr', 'danger', 'Erro ao alterar senha', 'icon-alert-circle-exc');
    } finally {
      setSavingOwnPassword(false);
    }
  };

  return (
    <div className="content">
      <style>{`
        .super-admin-table-body,
        .super-admin-table-wrap {
          overflow: visible !important;
        }
        .super-admin-actions-cell {
          overflow: visible;
        }
        .super-admin-actions-cell .dropdown-menu {
          z-index: 1050;
        }
        .super-admin-toggle-box {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px 14px;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        .super-admin-toggle-box input {
          margin-top: 3px;
          flex-shrink: 0;
        }
      `}</style>
      <Row className="mb-4 align-items-center">
        <Col lg="5" md="12">
          <h2 className="mb-1">Painel Super Admin</h2>
          <p className="text-muted mb-0">
            Gerencie todas as contas da plataforma · {user?.email}
          </p>
        </Col>
        <Col lg="7" md="12" className="text-lg-right mt-3 mt-lg-0">
          <Button
            color="secondary"
            outline
            className="mr-2 position-relative"
            onClick={() => navigate('/superadmin/leads')}
          >
            Leads
            {(stats?.leadsThisWeek ?? 0) > 0 && (
              <Badge
                color="danger"
                pill
                className="position-absolute"
                style={{ top: -8, right: -8, fontSize: 11 }}
              >
                {stats.leadsThisWeek}
              </Badge>
            )}
          </Button>
          <Button
            color="secondary"
            outline
            className="mr-2"
            onClick={() => navigate('/superadmin/planos')}
          >
            Planos
          </Button>
          <Button
            color="secondary"
            outline
            className="mr-2"
            onClick={() => navigate('/superadmin/miranda')}
          >
            Miranda AI
          </Button>
          <Button color="secondary" outline className="mr-2" onClick={() => setOwnPasswordOpen(true)}>
            Alterar minha senha
          </Button>
          <Button color="primary" onClick={() => setCreateOpen(true)}>
            <i className="tim-icons icon-simple-add mr-1" />
            Nova conta
          </Button>
        </Col>
      </Row>

      <Row>
        {[
          { label: 'Total de contas', value: stats?.totalAccounts ?? '—', icon: 'icon-bank' },
          { label: 'Contas ativas', value: stats?.activeAccounts ?? '—', icon: 'icon-check-2' },
          { label: 'Contas bloqueadas', value: stats?.blockedAccounts ?? '—', icon: 'icon-lock-circle' },
          { label: 'Pedidos (30 dias)', value: stats?.ordersLast30Days ?? '—', icon: 'icon-paper' },
          { label: 'Faturamento (30 dias)', value: stats ? formatMoney(stats.revenueLast30DaysCents) : '—', icon: 'icon-money-coins' },
        ].map((card) => (
          <Col lg="4" md="6" key={card.label} className="mb-4">
            <Card className="card-stats">
              <CardBody>
                <Row>
                  <Col xs="5">
                    <div className="icon-big text-center icon-warning">
                      <i className={`tim-icons ${card.icon}`} />
                    </div>
                  </Col>
                  <Col xs="7">
                    <div className="numbers">
                      <p className="card-category">{card.label}</p>
                      <h4 className="card-title">{card.value}</h4>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      <Card>
        <CardHeader>
          <Row className="align-items-center">
            <Col md="6">
              <h4 className="card-title mb-md-0">Contas</h4>
            </Col>
            <Col md="6" className="mt-3 mt-md-0">
              <Input
                placeholder="Buscar por nome, CNPJ ou e-mail..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load()}
              />
            </Col>
          </Row>
        </CardHeader>
        <CardBody className="super-admin-table-body">
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <div className="super-admin-table-wrap">
            <Table hover className="tablesorter mb-0">
              <thead className="text-primary">
                <tr>
                  <th>ID</th>
                  <th>Estabelecimento</th>
                  <th>Telefone</th>
                  <th>Admin</th>
                  <th>Status</th>
                  <th>Pedidos 30d</th>
                  <th>Faturamento 30d</th>
                  <th>Criada em</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {accounts.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center text-muted py-4">
                      Nenhuma conta encontrada
                    </td>
                  </tr>
                )}
                {accounts.map((acc) => (
                  <tr key={acc.id}>
                    <td>{acc.id}</td>
                    <td>
                      <strong>{acc.name}</strong>
                      {acc.isPlatform && (
                        <>
                          {' '}
                          <Badge color="info" pill className="ml-1">
                            Plataforma
                          </Badge>
                        </>
                      )}
                      <br />
                      <small className="text-muted">{acc.cnpj} · {ACCOUNT_TYPE_LABEL[acc.type] || acc.type}</small>
                      {acc.plan && (
                        <>
                          <br />
                          <Badge
                            pill
                            className="mt-1"
                            style={{
                              background: acc.plan.badgeColor || '#64748b',
                              color: '#fff',
                            }}
                          >
                            {acc.plan.name}
                          </Badge>
                        </>
                      )}
                    </td>
                    <td>{acc.phone ? formatPhone(acc.phone) : '—'}</td>
                    <td>
                      {acc.admin ? (
                        <>
                          {acc.admin.name}
                          <br />
                          <small className="text-muted">{acc.admin.email}</small>
                        </>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {acc.adminBlocked ? (
                        <Badge color="danger">Bloqueada</Badge>
                      ) : (
                        <Badge color="success">Ativa</Badge>
                      )}
                      {acc.ai?.enabled && (
                        <Badge color="info" className="ml-1" title="Miranda AI habilitado">
                          Miranda AI
                        </Badge>
                      )}
                      {acc.botEnabled && (
                        <Badge color="primary" className="ml-1" title="Miranda Bot habilitado">
                          Miranda Bot
                        </Badge>
                      )}
                      {acc.whatsappProvider === 'waba' && (
                        <Badge color="success" className="ml-1" title="WhatsApp Cloud API oficial">
                          WABA
                        </Badge>
                      )}
                    </td>
                    <td>{acc.ordersLast30Days ?? 0}</td>
                    <td>{formatMoney(acc.revenueLast30DaysCents ?? 0)}</td>
                    <td>{formatDate(acc.createdAt)}</td>
                    <td className="text-right super-admin-actions-cell">
                      <UncontrolledDropdown direction="up">
                        <DropdownToggle className="btn-link" color="default" caret>
                          Ações
                        </DropdownToggle>
                        <DropdownMenu right>
                          <DropdownItem onClick={() => navigate(accountPath(acc.id, '/dashboard'))}>
                            Acessar sistema
                          </DropdownItem>
                          {!acc.isPlatform && (
                            <>
                              <DropdownItem onClick={() => openEdit(acc)}>Editar</DropdownItem>
                              <DropdownItem onClick={() => openResetPassword(acc)}>
                                Redefinir senha admin
                              </DropdownItem>
                              <DropdownItem divider />
                              <DropdownItem
                                onClick={() => toggleBlock(acc)}
                                className={acc.adminBlocked ? 'text-success' : 'text-danger'}
                              >
                                {acc.adminBlocked ? 'Desbloquear' : 'Bloquear'}
                              </DropdownItem>
                            </>
                          )}
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        isOpen={createOpen}
        toggle={() => setCreateOpen(false)}
        size="lg"
        className="super-admin-dialog"
      >
        <ModalHeader toggle={() => setCreateOpen(false)}>Nova conta</ModalHeader>
        <ModalBody>
          <Form id="super-admin-create-account-form" onSubmit={handleCreate}>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Nome do estabelecimento</Label>
                  <Input required value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>CNPJ</Label>
                  <Input required value={createForm.cnpj} onChange={(e) => setCreateForm({ ...createForm, cnpj: e.target.value })} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Telefone</Label>
                  <Input
                    required
                    type="tel"
                    value={createForm.phone}
                    onChange={(e) => setCreateForm({ ...createForm, phone: maskPhoneInput(e.target.value) })}
                    placeholder="(31) 99999-9999"
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Tipo</Label>
                  <Input type="select" value={createForm.type} onChange={(e) => setCreateForm({ ...createForm, type: e.target.value })}>
                    {ACCOUNT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Plano</Label>
                  <Input
                    type="select"
                    value={createForm.planId === null || createForm.planId === undefined ? '' : createForm.planId}
                    onChange={(e) => setCreateForm({ ...createForm, planId: e.target.value })}
                  >
                    <option value="">Sem plano</option>
                    {plans.filter((p) => p.active).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} — {formatMoney(p.priceCents)}
                      </option>
                    ))}
                  </Input>
                  <small className="text-muted">
                    Features, IA e modelo vêm do plano.
                  </small>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Provedor WhatsApp</Label>
                  <Input
                    type="select"
                    value={createForm.whatsappProvider}
                    onChange={(e) => setCreateForm({ ...createForm, whatsappProvider: e.target.value })}
                  >
                    {WHATSAPP_PROVIDERS.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </Input>
                  <small className="text-muted">
                    WABA exige as credenciais da Meta na tela de Conexão WhatsApp.
                  </small>
                </FormGroup>
              </Col>
              {selectedCreatePlan && (
                <Col md="12">
                  <div
                    className="mb-3 p-3"
                    style={{
                      borderRadius: 8,
                      border: '1px solid rgba(255,255,255,0.1)',
                      background: 'rgba(255,255,255,0.04)',
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap" style={{ gap: 8 }}>
                      <strong>Features do plano</strong>
                      <small className="text-muted">
                        IA: {selectedCreatePlan.aiProvider || 'DEEPSEEK'} / {selectedCreatePlan.aiModelId || '—'}
                      </small>
                    </div>
                    <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                      {selectedCreatePlanFeatures.map((f) => (
                        <Badge key={f.featureKey} color="info" pill>
                          {f.featureKey}
                          {f.limitValue != null ? ` (${f.limitValue})` : ''}
                        </Badge>
                      ))}
                      {!selectedCreatePlanFeatures.length && (
                        <span className="text-muted small">Nenhuma feature ativa</span>
                      )}
                    </div>
                  </div>
                </Col>
              )}
              <Col md="6">
                <FormGroup>
                  <Label>Nome do admin</Label>
                  <Input required value={createForm.adminName} onChange={(e) => setCreateForm({ ...createForm, adminName: e.target.value })} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>E-mail do admin</Label>
                  <Input type="email" required value={createForm.adminEmail} onChange={(e) => setCreateForm({ ...createForm, adminEmail: e.target.value })} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Senha do admin</Label>
                  <Input type="password" required minLength={6} value={createForm.adminPassword} onChange={(e) => setCreateForm({ ...createForm, adminPassword: e.target.value })} />
                </FormGroup>
              </Col>
            </Row>
          </Form>
        </ModalBody>
        <ModalFooter className="super-admin-modal-footer">
          <Button color="secondary" outline type="button" onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button color="primary" type="submit" form="super-admin-create-account-form" disabled={creating}>
            {creating ? 'Criando...' : 'Criar conta'}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={editOpen}
        toggle={() => setEditOpen(false)}
        size="lg"
        className="super-admin-dialog"
      >
        <ModalHeader toggle={() => setEditOpen(false)}>Editar conta</ModalHeader>
        <ModalBody>
          <Form id="super-admin-edit-account-form" onSubmit={handleEdit}>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Nome do estabelecimento</Label>
                  <Input required value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>CNPJ</Label>
                  <Input required value={editForm.cnpj || ''} onChange={(e) => setEditForm({ ...editForm, cnpj: e.target.value })} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Tipo</Label>
                  <Input type="select" value={editForm.type || 'RESTAURANTE'} onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}>
                    {ACCOUNT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Telefone</Label>
                  <Input
                    type="tel"
                    value={editForm.phone || ''}
                    onChange={(e) => setEditForm({ ...editForm, phone: maskPhoneInput(e.target.value) })}
                    placeholder="(31) 99999-9999"
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Plano</Label>
                  <Input
                    type="select"
                    value={editForm.planId === null || editForm.planId === undefined ? '' : editForm.planId}
                    onChange={(e) => {
                      const value = e.target.value;
                      const plan = plans.find((p) => p.id === Number(value));
                      const chatFeat = (plan?.features || []).find(
                        (f) => f.featureKey === 'ai_advanced_chat' && f.enabled,
                      );
                      setEditForm({
                        ...editForm,
                        planId: value,
                        aiChatMonthlyLimit: chatFeat
                          ? chatFeat.limitValue ?? 100
                          : editForm.aiChatMonthlyLimit,
                      });
                    }}
                  >
                    <option value="">Sem plano</option>
                    {plans
                      .filter((p) => p.active || p.id === Number(editForm.planId))
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} — {formatMoney(p.priceCents)}
                        </option>
                      ))}
                  </Input>
                  <small className="text-muted">
                    Features, IA e modelo vêm do plano. Edite em Planos.
                  </small>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Provedor WhatsApp</Label>
                  <Input
                    type="select"
                    value={editForm.whatsappProvider || 'baileys'}
                    onChange={(e) => setEditForm({ ...editForm, whatsappProvider: e.target.value })}
                  >
                    {WHATSAPP_PROVIDERS.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </Input>
                  <small
                    className={
                      editAccount && editForm.whatsappProvider !== (editAccount.whatsappProvider || 'baileys')
                        ? 'text-warning'
                        : 'text-muted'
                    }
                  >
                    {editAccount && editForm.whatsappProvider !== (editAccount.whatsappProvider || 'baileys')
                      ? 'Ao salvar, a conexão atual será desconectada.'
                      : 'WABA exige as credenciais da Meta na tela de Conexão WhatsApp.'}
                  </small>
                </FormGroup>
              </Col>
              {selectedPlanHasAiChat && (
                <Col md="6">
                  <FormGroup>
                    <Label>Limite de requests IA (chat) / mês</Label>
                    <Input
                      type="number"
                      min={0}
                      value={editForm.aiChatMonthlyLimit ?? 100}
                      onChange={(e) =>
                        setEditForm({ ...editForm, aiChatMonthlyLimit: e.target.value })
                      }
                    />
                    <small className="text-muted">Override da conta. Reseta todo dia 1.</small>
                  </FormGroup>
                </Col>
              )}
              <Col md="12">
                <div className="super-admin-toggle-box mt-2">
                  <Input
                    type="checkbox"
                    id="botEnabled"
                    checked={!!editForm.botEnabled}
                    onChange={(e) => setEditForm({ ...editForm, botEnabled: e.target.checked })}
                  />
                  <div>
                    <Label for="botEnabled" className="mb-1" style={{ cursor: 'pointer' }}>
                      Miranda Bot
                    </Label>
                    <div className="text-muted" style={{ fontSize: 13, lineHeight: 1.45 }}>
                      Libera o atendimento automático no WhatsApp desta conta.
                      O estabelecimento configura o ID do Miranda Bot na tela de WhatsApp.
                    </div>
                  </div>
                </div>
              </Col>
              {selectedEditPlan && (
                <Col md="12">
                  <div
                    className="mt-2 p-3"
                    style={{
                      borderRadius: 8,
                      border: '1px solid rgba(255,255,255,0.1)',
                      background: 'rgba(255,255,255,0.04)',
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap" style={{ gap: 8 }}>
                      <strong>Features do plano</strong>
                      <small className="text-muted">
                        IA: {selectedEditPlan.aiProvider || 'DEEPSEEK'} / {selectedEditPlan.aiModelId || '—'}
                      </small>
                    </div>
                    <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                      {selectedPlanFeatures.map((f) => (
                        <Badge key={f.featureKey} color="info" pill>
                          {f.featureKey}
                          {f.limitValue != null ? ` (${f.limitValue})` : ''}
                        </Badge>
                      ))}
                      {!selectedPlanFeatures.length && (
                        <span className="text-muted small">Nenhuma feature ativa</span>
                      )}
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </Form>
        </ModalBody>
        <ModalFooter className="super-admin-modal-footer">
          <Button color="secondary" outline type="button" onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button color="primary" type="submit" form="super-admin-edit-account-form" disabled={savingEdit}>
            {savingEdit ? 'Salvando...' : 'Salvar'}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={passwordOpen} toggle={() => setPasswordOpen(false)} className="super-admin-dialog">
        <ModalHeader toggle={() => setPasswordOpen(false)}>
          Redefinir senha — {passwordTarget?.name}
        </ModalHeader>
        <ModalBody>
          <Form id="super-admin-reset-password-form" onSubmit={handleResetPassword}>
            <p className="text-muted">
              Admin: {passwordTarget?.admin?.email}
            </p>
            <FormGroup>
              <Label>Nova senha</Label>
              <Input type="password" required minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter className="super-admin-modal-footer">
          <Button color="secondary" outline onClick={() => setPasswordOpen(false)}>Cancelar</Button>
          <Button color="primary" type="submit" form="super-admin-reset-password-form" disabled={savingPassword}>
            {savingPassword ? 'Salvando...' : 'Redefinir'}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={ownPasswordOpen} toggle={() => setOwnPasswordOpen(false)} className="super-admin-dialog">
        <ModalHeader toggle={() => setOwnPasswordOpen(false)}>Alterar minha senha</ModalHeader>
        <ModalBody>
          <Form id="super-admin-own-password-form" onSubmit={handleOwnPassword}>
            <FormGroup>
              <Label>Nova senha</Label>
              <Input type="password" required minLength={6} value={ownPassword} onChange={(e) => setOwnPassword(e.target.value)} />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter className="super-admin-modal-footer">
          <Button color="secondary" outline onClick={() => setOwnPasswordOpen(false)}>Cancelar</Button>
          <Button color="primary" type="submit" form="super-admin-own-password-form" disabled={savingOwnPassword}>
            {savingOwnPassword ? 'Salvando...' : 'Alterar'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default SuperAdminPage;
